'use client';

import { ArrowUpRight, ArrowRight, BookOpen, Bot, Building2, MapPin, Rocket, Sparkles, ThumbsUp, TrendingUp, Users } from 'lucide-react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from "next/link";
import { useState, useEffect, useRef } from 'react';
import CredentialsStrip from '@/components/shared/CredentialsStrip';
import { companyAge } from "@utils/companyAge";
import { platformReviews } from '@/data/platformReviewsData';
const SPHERE_SIZE = 480;
const SpherePlaceholder = () => (
  <div style={{ width: SPHERE_SIZE, height: SPHERE_SIZE, maxWidth: '100%' }} aria-hidden="true" />
);
const UniverseSphere = dynamic(() => import('@shared/UniverseSphere'), { ssr: false, loading: SpherePlaceholder });

const BADGES = [
  { Icon: Users, tag: 'Hire', text: 'Dedicated Offshore Developers for Your Team', href: '/hire-full-stack-developers' },
  { Icon: Bot, tag: 'AI Agents', text: 'Automate Workflows with Custom AI Agents', href: '/ai-agents-development' },
  { Icon: Sparkles, tag: 'Gen AI', text: 'Generative AI Solutions Built for Your Business', href: '/generative-ai-development-services' },
  { Icon: BookOpen, tag: 'Case Studies', text: 'See How We Help Startups & Enterprises Grow', href: '/case-studies' },
  { Icon: Building2, tag: 'Industries', text: 'Software Built for Your Industry', href: '/industries' },
];

const SLIDES = [
  {
    line1: 'Outsourced AI-Driven',
    accent: 'Software Development Services',
    line3: 'for the Next Era of Business',
    desc: 'Combining AI innovation and custom software development to streamline operations, create smarter digital experiences, and drive business growth.',
  },
];

const STATS = [
  { Icon: TrendingUp, value: `${companyAge}+`, label: 'Years of Experience' },
  { Icon: Rocket, value: '500+', label: 'Projects Delivered' },
  { Icon: ThumbsUp, value: '98%', label: 'Client Satisfaction' },
  { Icon: MapPin, value: '12+', label: 'Countries Served' },
];

const REVIEW_PLATFORMS = ['Clutch', 'Google', 'Trustpilot', 'RightFirms'];
const reviewIcons = platformReviews.filter((r) => REVIEW_PLATFORMS.includes(r.platform));

const INTERVAL = 4500;
const BADGE_INTERVAL = 3500;

const glassCard = 'rounded-2xl border border-white/15 bg-white/[0.07] backdrop-blur-md shadow-xl shadow-black/30';

export default function HeroSectionV4() {
  const [current, setCurrent] = useState(0);
  const [tick, setTick] = useState(0); // increment to re-trigger animation
  const [isPaused, setIsPaused] = useState(false);
  const [badgeIndex, setBadgeIndex] = useState(0);
  const [badgePaused, setBadgePaused] = useState(false);
  const [showSphere, setShowSphere] = useState(false);
  const slideRef = useRef(null);
  const stripeRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    let idleId;
    const schedule = () => {
      if (!mq.matches) return;
      const ric = window.requestIdleCallback ?? (cb => setTimeout(cb, 1200));
      idleId = ric(() => setShowSphere(true));
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    const onChange = () => { if (mq.matches) setShowSphere(true); };
    mq.addEventListener('change', onChange);
    return () => {
      window.removeEventListener('load', schedule);
      mq.removeEventListener('change', onChange);
      if (idleId) (window.cancelIdleCallback ?? clearTimeout)(idleId);
    };
  }, []);

  useEffect(() => {
    if (tick === 0) return;
    slideRef.current?.animate(
      [{ opacity: 0, transform: 'translateY(28px)' }, { opacity: 1, transform: 'translateY(0)' }],
      { duration: 650, easing: 'ease', fill: 'both' }
    );
    stripeRef.current?.animate(
      [{ transform: 'scaleX(0)', opacity: 0 }, { transform: 'scaleX(1)', opacity: 0.65 }],
      { duration: 800, delay: 550, easing: 'ease-out', fill: 'both' }
    );
  }, [tick]);

  useEffect(() => {
    if (isPaused || SLIDES.length < 2) return;
    const id = setInterval(() => {
      setCurrent(c => (c + 1) % SLIDES.length);
      setTick(t => t + 1);
    }, INTERVAL);
    return () => clearInterval(id);
  }, [isPaused]);

  useEffect(() => {
    if (badgePaused || BADGES.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setBadgeIndex(i => (i + 1) % BADGES.length), BADGE_INTERVAL);
    return () => clearInterval(id);
  }, [badgePaused]);

  const slide = SLIDES[current] ?? SLIDES[0];
  const badge = BADGES[badgeIndex];
  const HeadingTag = current === 0 ? 'h1' : 'h2';

  return (
    <section
      aria-labelledby="home-hero-heading"
      className="relative overflow-hidden min-h-svh flex flex-col bg-linear-to-b from-slate-950 via-blue-950 to-slate-950"
    >
      <div className="absolute inset-x-0 top-0 h-[560px] bg-[radial-gradient(ellipse_at_top,rgba(224,39,74,0.09),transparent_60%)] pointer-events-none" aria-hidden="true" />
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        aria-hidden="true"
        style={{
          maskImage: 'radial-gradient(ellipse 55% 50% at 50% 42%, transparent 30%, black 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 55% 50% at 50% 42%, transparent 30%, black 100%)',
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.045]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            backgroundPosition: '50% 0',
          }}
        />
        {[
          { top: '192px', delay: '0s' },
          { top: '512px', delay: '4.5s' },
        ].map((b) => (
          <span
            key={b.top}
            className="hero-beam-x absolute left-0 h-px w-[22vw] bg-linear-to-r from-transparent via-white/40 to-transparent"
            style={{ top: b.top, animationDelay: b.delay }}
          />
        ))}
        {[
          { left: 'calc(50% - 672px)', delay: '1.5s' },
          { left: 'calc(50% + 608px)', delay: '5.5s' },
        ].map((b) => (
          <span
            key={b.left}
            className="hero-beam-y absolute top-0 w-px h-[26vh] bg-linear-to-b from-transparent via-red-300/45 to-transparent"
            style={{ left: b.left, animationDelay: b.delay }}
          />
        ))}
      </div>
      <div className="absolute top-1/3 -left-32 w-80 h-80 bg-red-500/[0.07] rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute top-1/2 -right-32 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-[1440px] mx-auto w-full px-6 pt-32 sm:pt-36 lg:pt-36 pb-16 lg:pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_1px_minmax(0,1fr)] gap-0 items-center">

          {/* Left — badge, headline, CTAs, reviews, service quick-links */}
          <div className="flex flex-col gap-6 sm:gap-7 lg:pr-14">
            <div ref={slideRef} className="flex flex-col items-start gap-5 sm:gap-6">
              <Link
                key={badge.href}
                href={badge.href}
                onMouseEnter={() => setBadgePaused(true)}
                onMouseLeave={() => setBadgePaused(false)}
                onFocus={() => setBadgePaused(true)}
                onBlur={() => setBadgePaused(false)}
                className="hero-badge group inline-flex max-w-full items-center gap-2 sm:gap-3 rounded-full border border-white/15 bg-white/[0.06] p-1.5 pr-3 sm:pr-4 backdrop-blur-sm hover:border-red-400/50 hover:bg-white/10 transition-colors duration-200"
              >
                <span className="inline-flex shrink-0 items-center justify-center gap-1 h-6 rounded-full bg-primary text-white pl-2 pr-2.5 text-[11px] font-semibold uppercase tracking-wider leading-none whitespace-nowrap">
                  <badge.Icon size={12} className="shrink-0" aria-hidden="true" />
                  {badge.tag}
                </span>
                <span className="min-w-0 truncate text-xs sm:text-sm text-blue-100/80 group-hover:text-white transition-colors">
                  {badge.text}
                </span>
                <ArrowRight size={14} className="shrink-0 text-blue-100/60 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-white" />
              </Link>
              <HeadingTag id="home-hero-heading" className="font-heading font-extrabold text-[2rem] sm:text-5xl lg:text-[2.5rem] xl:text-[2.5rem] 2xl:text-[2.80rem] text-white leading-[1.20] tracking-tight">
                <span className="block">{slide.line1}</span>{' '}
                <span className="inline-block relative pb-2">
                  <em className="not-italic bg-linear-to-r from-red-400 via-red-500 to-rose-400 bg-clip-text text-transparent">
                    {slide.accent}
                  </em>
                  <span
                    ref={stripeRef}
                    className="absolute left-0 bottom-2 h-1.5 w-full origin-left rounded-full pointer-events-none"
                    style={{
                      background: 'linear-gradient(90deg, #E0274A, transparent)',
                      animation: 'stripe-expand 0.8s ease-out 0.55s both',
                    }}
                  />
                </span>
                {' '}{slide.line3.trim()}
              </HeadingTag>
              <p className="text-blue-100/75 text-base sm:text-lg max-w-xl leading-snug text-pretty">
                {slide.desc}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                href="/contact-us"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
                className="btn-glass group relative overflow-hidden flex items-center justify-center gap-2 px-7 py-3.5 bg-linear-to-r from-primary to-rose-600 text-white font-semibold rounded-xl cursor-pointer shadow-lg shadow-red-900/40"
              >
                <span className="relative z-10 flex items-center gap-2 group-hover:text-secondary transition-colors duration-400">
                  Get a Free Consultation
                  <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
              <Link
                href="/portfolio"
                className="group flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl border border-white/20 bg-white/5 text-white font-semibold backdrop-blur-sm hover:bg-white/10 hover:border-white/35 transition-colors duration-200"
              >
                View Our Work
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
            <Link href="/client-reviews" className="group self-start flex items-center gap-3">
              <span className="flex -space-x-2">
                {reviewIcons.map((r) => (
                  <span key={r.platform} className="size-9 rounded-full bg-white ring-2 ring-blue-950 flex items-center justify-center" title={r.platform}>
                    <Image src={r.icon} alt={`${r.platform} reviews`} width={20} height={20} draggable="false" className="size-5 object-contain" />
                  </span>
                ))}
              </span>
              <span className="text-sm text-blue-200/80 leading-snug">
                Verified client reviews on{' '}
                <span className="text-white font-semibold group-hover:text-red-400 transition-colors">Clutch, Google, Trustpilot &amp; more</span>
              </span>
            </Link>
          </div>
          <div
            className="hidden lg:block h-full min-h-105 w-px"
            aria-hidden="true"
            style={{
              background: 'linear-gradient(180deg, transparent 0%, rgba(129,140,248,0.3) 20%, rgba(129,140,248,0.7) 50%, rgba(129,140,248,0.3) 80%, transparent 100%)',
            }}
          />
          <div className="flex flex-col items-center justify-center mt-10 sm:mt-12 lg:mt-0 lg:pl-14">
            <div className="hidden lg:block relative w-full max-w-105 mx-auto -top-8">
              <div className="absolute top-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse motion-reduce:animate-none pointer-events-none" aria-hidden="true" />
              {showSphere ? (
                <UniverseSphere
                  size={SPHERE_SIZE}
                  isDark
                  logoSrc="/images/VelanApps-Icon.webp"
                />
              ) : (
                <SpherePlaceholder />
              )}
            </div>
            <dl className={`relative lg:-mt-6 w-full grid grid-cols-2 sm:grid-cols-4 ${glassCard}`}>
              {STATS.map(({ Icon, value, label }, i) => (
                <div
                  key={label}
                  className={`flex flex-col items-center gap-2 px-2 py-4 xl:px-4 border-white/10 ${i % 2 === 0 ? 'border-r' : i < STATS.length - 1 ? 'sm:border-r' : ''
                    } ${i < 2 ? 'border-b sm:border-b-0' : ''}`}
                >
                  <dt className="order-2 text-sm xl:text-xs text-blue-200/100 text-center leading-tight">{label}</dt>
                  <dd className="order-1 flex items-center gap-1.5">
                    <Icon size={18} className="text-red-500 shrink-0" aria-hidden="true" />
                    <span className="text-xl xl:text-2xl font-extrabold text-white leading-none">{value}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <CredentialsStrip />
      </div>
    </section>
  );
}
